// Quando a câmera se mexe — e quando ela fica quieta, que é a parte nova.
//
// Antes ela CENTRAVA o nó atual a cada avanço de fase, e isso apagava a caminhada:
// a pista rolava no mesmo sentido e na mesma hora que o explorador andava, então o
// deslocamento dele na FAIXA era 184px e na TELA quase zero. Medido em 03/08 com
// `ferramentas-diagnostico/harness-trilha`: 184px de percurso, 22px na tela, 206px
// de câmera. O avatar andava e o aluno via o cenário deslizar por baixo de um
// boneco parado.
//
// A regra aqui é a do gênero: a câmera tem uma ZONA MORTA no meio da pista e só
// reage quando o herói sai dela. Dentro da zona, o explorador atravessa a tela e a
// câmera não faz nada — é a caminhada aparecendo. Fora, a câmera reenquadra.
//
// Reenquadrar CENTRANDO, e não trazendo o nó de volta à borda da zona pelo mínimo:
// pelo mínimo, o avanço seguinte sairia da zona de novo e a câmera voltaria a rolar
// a cada passo, que é o defeito de origem. Centrado, o explorador ganha meia pista
// de folga à frente e caminha vários nós antes do próximo reenquadramento.

// Fração da largura da pista reservada em CADA borda. Com 0,28 a zona morta ocupa
// os 44% centrais: numa pista de 1280 com nós a 184px, isso são ~3 avanços dentro
// da zona por reenquadramento.
//
// Não pode ser muito maior: quanto mais larga a zona, mais perto da borda o nó
// atual chega antes de a câmera reagir, e o nó seguinte (o próximo destino do
// aluno) fica fora de vista — a trilha passa a parecer que acabou ali.
export const MARGEM_DA_ZONA_MORTA = 0.28;

/**
 * Para onde rolar a pista, ou `null` quando não há motivo para mexer.
 *
 * Trabalha em px de conteúdo (a mesma régua de `scrollLeft`), e recebe o limite de
 * rolagem em vez de medi-lo: `scrollWidth` é zero em jsdom, e a largura da faixa o
 * componente já conhece — quem calcula é `distribuir`.
 *
 * @param {number} centroDoNo     px do centro do nó atual dentro da faixa
 * @param {number} larguraDaPista px visíveis da pista
 * @param {number} scrollAtual    px já rolados
 * @param {number} larguraDaFaixa px do percurso inteiro
 */
export function destinoDaCamera({
  centroDoNo,
  larguraDaPista,
  scrollAtual,
  larguraDaFaixa
}) {
  if (!larguraDaPista) return null;

  const margem = larguraDaPista * MARGEM_DA_ZONA_MORTA;
  const naTela = centroDoNo - scrollAtual;
  if (naTela >= margem && naTela <= larguraDaPista - margem) return null;

  const limite = Math.max(0, larguraDaFaixa - larguraDaPista);
  const destino = Math.max(
    0,
    Math.min(limite, centroDoNo - larguraDaPista / 2)
  );
  // Menos de um pixel de diferença não é movimento — e mandar um `scrollTo` suave
  // para onde a pista já está acorda o listener de scroll à toa.
  return Math.abs(destino - scrollAtual) < 1 ? null : destino;
}

// Meio nó (36px) mais uma folga: um nó que aparece pela metade na borda não serve
// de destino visível, e é onde o rótulo já sai cortado.
const FOLGA_DE_BORDA = 48;

/**
 * O nó está dentro da pista, agora?
 *
 * É o que decide se o reenquadramento pode ESPERAR a caminhada. O nó atual é o
 * destino do explorador: se ele está à vista, o aluno assiste à chegada e a câmera
 * pode se mexer depois. Se está fora da pista — telas estreitas, onde a zona morta
 * é menor que um vão inteiro —, esperar faria o avatar caminhar para fora da tela,
 * e aí é melhor rolar junto e mostrar menos deslocamento do que mostrar nada.
 */
export function noEstaNaTela({ centroDoNo, larguraDaPista, scrollAtual }) {
  const naTela = centroDoNo - scrollAtual;
  return naTela >= FOLGA_DE_BORDA && naTela <= larguraDaPista - FOLGA_DE_BORDA;
}
